'use client'

import { useMedia } from '@/hooks'
import { Locale, MEDIA } from '@spot'
import { memo, useEffect } from 'react'

interface Props {
  lang?: Locale
  categoryId: string
  appId: string
}

const GuuruChat = ({ lang = 'de', categoryId, appId }: Props) => {
  const isDesktop = useMedia(MEDIA.desktop, true)

  // Cleanup function to remove the Guuru script and reset its state
  const cleanupGuuru = () => {
    if (typeof window.guuru !== 'undefined') {
      window.guuru = null
    }
    const existingScript = document.querySelector(
      'script[src="https://static.guuru.com/loader/v1.0/chat.min.js"]'
    )
    if (existingScript) {
      existingScript.remove()
    }
  }

  useEffect(() => {
    // Skip initialization if not on desktop
    if (!isDesktop) {
      cleanupGuuru()
      return
    }

    // Check if script already exists
    if (
      document.querySelector(
        'script[src="https://static.guuru.com/loader/v1.0/chat.min.js"]'
      )
    ) {
      console.log('Guuru script already loaded.')
      return
    }

    const script = document.createElement('script')
    script.src = 'https://static.guuru.com/loader/v1.0/chat.min.js'
    script.async = true

    script.onload = () => {
      if (typeof window.Guuru !== 'undefined') {
        console.log('Guuru loaded successfully.')
        window.guuru = window.Guuru()
        window.guuru.init({
          appId,
          locale: lang,
          category: categoryId
        })
      } else {
        console.error('Guuru is defined but could not initialize.')
      }
    }

    script.onerror = () => {
      console.error('Failed to load the Guuru script.')
    }

    document.head.appendChild(script)

    return () => {
      cleanupGuuru()
    }
  }, [isDesktop, appId, lang, categoryId]) // Dependencies

  return null // No visible UI elements
}

export default memo(GuuruChat)
